// The Waivers log's filter chips, and the two lines that say what the log
// holds (B-137): the empty state, and the note under a log that is not the
// whole log, above its Older entries (B-526). Pure, so the lines can be held
// to what is on screen.

import type { LogEntry } from '@fsp/domain';

export type LogFilter = 'all' | 'mine' | 'adds' | 'drops' | 'claims';

export function matchesFilter(e: LogEntry, f: LogFilter): boolean {
  if (f === 'all') return true;
  if (e.kind === 'run')
    return f === 'claims' || (f === 'mine' && e.run.results.some((r) => r.isMine));
  const tx = e.transaction;
  if (f === 'mine') return tx.team.isMine;
  if (f === 'adds') return tx.kind === 'add';
  if (f === 'drops') return tx.kind === 'drop';
  return false;
}

/** What each filter keeps, as a noun for "No ‹these› in the log". */
const KEPT: Record<Exclude<LogFilter, 'all'>, string> = {
  mine: 'moves of yours',
  adds: 'adds',
  drops: 'drops',
  claims: 'claims',
};

/**
 * The log's empty line and its note, for `shown` entries matching `filter` out
 * of `carried` on screen so far, `more` when older ones are still to read
 * (Older entries, under the lines, reads them: B-526).
 *
 * Under a filter neither line may claim more than it knows: an empty filter is
 * not an empty log, and "the newest 100" is how many were searched, not how
 * many are on screen. The note shows only under rows, so an empty filter says
 * the searched count once, in its own line.
 */
export function logNotes(
  filter: LogFilter,
  shown: number,
  carried: number,
  more: boolean,
): { empty: string | null; note: string | null } {
  if (shown === 0)
    return {
      empty:
        filter === 'all'
          ? 'Nothing in the log yet.'
          : more
            ? `No ${KEPT[filter]} in the newest ${carried} entries.`
            : `No ${KEPT[filter]} in the log yet.`,
      note: null,
    };
  if (!more) return { empty: null, note: null };
  return {
    empty: null,
    note:
      filter === 'all' ? `Showing the newest ${carried}.` : `From the newest ${carried} entries.`,
  };
}
